// Candidate confidence score (spec: Ingestion and matching -> Confidence score).
// Weights and thresholds are the spec's proposed starting points, to be tuned on a labelled set.

import type { Candidate, ConfidenceLabel, ScoredCandidate } from './types.ts';
import { normalizeModel, resolveMake } from './normalize.ts';

export const WEIGHTS = {
  exactModel: 0.4,
  make: 0.15,
  sourceTrust: 0.15,
  identifier: 0.15,
  variant: 0.1,
  textMention: 0.05,
} as const;

export const THRESHOLDS = { matched: 0.9, likely: 0.6 } as const;

/** manufacturer > public repository > retailer (spec). User uploads are the user's own word. */
export type TrustTier = 'manufacturer' | 'repository' | 'retailer' | 'user';
const TRUST: Record<TrustTier, number> = { manufacturer: 1, user: 1, repository: 0.6, retailer: 0.4 };

export interface MatchQuery {
  make?: string;
  model: string;
  identifiers?: string[];
}

export function labelFor(score: number): ConfidenceLabel {
  if (score >= THRESHOLDS.matched) return 'matched';
  if (score >= THRESHOLDS.likely) return 'likely';
  return 'possible';
}

export function scoreCandidate(q: MatchQuery, c: Candidate, tier: TrustTier): ScoredCandidate {
  const want = normalizeModel(q.model);
  const reasons: string[] = [];
  const signals: Record<string, number> = {};

  // Exact model: full match 1, same base with a different/missing variant 0.6.
  if (c.model) {
    const got = normalizeModel(c.model);
    if (got.normalized === want.normalized) signals.exactModel = 1;
    else if (got.base === want.base) {
      signals.exactModel = 0.6;
      reasons.push(`Model variant differs: ${got.normalized} vs ${want.normalized}`);
    } else {
      signals.exactModel = 0;
      reasons.push(`Different model: ${got.normalized}`);
    }
    // Variant agreement only counts when both sides name one, or neither does.
    signals.variant = got.variant === want.variant ? 1 : 0;
  } else {
    signals.exactModel = 0;
    signals.variant = 0;
    reasons.push('Source does not state a model number');
  }

  if (q.make && c.make) {
    const same = resolveMake(q.make).canonical === resolveMake(c.make).canonical;
    signals.make = same ? 1 : 0;
    if (!same) reasons.push(`Different make: ${c.make}`);
  } else {
    signals.make = 0;
    if (q.make) reasons.push('Source does not state a make');
  }

  signals.sourceTrust = TRUST[tier];
  if (tier === 'repository' || tier === 'retailer') reasons.push(`Source is a ${tier}, not the manufacturer`);

  const ids = new Set((q.identifiers ?? []).map((s) => s.trim()));
  signals.identifier = (c.identifiers ?? []).some((i) => ids.has(i.trim())) ? 1 : 0;

  // Title mention is a weak stand-in for "model on the cover page" until text is extracted.
  const title = c.title.toUpperCase().replace(/[\s\-_.]/g, '');
  signals.textMention = title.includes(want.base) ? 1 : 0;

  let score = 0;
  for (const [k, w] of Object.entries(WEIGHTS)) score += w * (signals[k] ?? 0);
  score = Math.round(score * 100) / 100;
  return { ...c, score, label: labelFor(score), signals, reasons };
}

/** Highest score first; ties keep the source's own order. */
export function rankCandidates(q: MatchQuery, cs: Array<{ c: Candidate; tier: TrustTier }>): ScoredCandidate[] {
  return cs
    .map(({ c, tier }, i) => ({ s: scoreCandidate(q, c, tier), i }))
    .sort((a, b) => b.s.score - a.s.score || a.i - b.i)
    .map(({ s }) => s);
}
